import { makeAutoObservable, runInAction } from "mobx";
import dayjs from "dayjs";
import SwageazyInventoryAdjustmentAPI, {
  parseInventoryAdjustmentError,
} from "../api/SwageazyInventoryAdjustmentAPI";
import SwageazyInventoryAPI from "../api/SwageazyInventoryAPI";
import CatalogProductsAPI from "../api/CatalogProductsAPI";
import WarehouseAPI from "../api/WarehouseAPI";
import { SortType } from "../../../models/SortType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  ProductListApi,
  ProductVariantApi,
} from "../../../models/CatalogProducts/types";
import { ProductStockDetails } from "../../../models/SwageazyInventory/SwageazyInventory";
import {
  INVENTORY_ADJUSTMENT_MAX_LINES,
  INVENTORY_ADJUSTMENT_NOTE_MAX,
  INVENTORY_ADJUSTMENT_REASON_MAX,
  INVENTORY_ADJUSTMENT_REASON_MIN,
  INVENTORY_BOX_ID_PATTERN,
  InventoryAdjustmentLineRequest,
  InventoryAdjustmentResult,
  InventoryAdjustmentRowError,
  InventoryAuditLogFilters,
  InventoryAuditLogRow,
  NewAdjustmentSkuLine,
  ProductTransactionFilters,
  ProductTransactionRow,
  SKU_TRANSFER_MAX_BOXES,
  SkuTransferRequest,
  SkuWarehouseSummary,
  StockAdjustmentRow,
} from "../../../models/SwageazyInventory/SwageazyInventoryAdjustment";

export interface AdjustmentTargetProduct {
  id: number;
  name: string;
}

export interface DestinationSkuOption {
  sku: string;
  label: string;
}

const AUDIT_PAGE_LIMIT = 20;

const rowKey = (sku: string, warehouseId: string, boxId: string) =>
  `${sku.toUpperCase()}|${warehouseId}|${boxId.toUpperCase()}`;

const skuWarehouseKey = (sku: string, warehouseId: string) =>
  `${sku.toUpperCase()}|${warehouseId}`;

const toInt = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

/** Accepts signed whole numbers only, e.g. "5", "-3". */
const parseSignedInt = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
};

const validateReason = (reason: string): string => {
  const length = reason.trim().length;
  if (length < INVENTORY_ADJUSTMENT_REASON_MIN)
    return `Reason must be at least ${INVENTORY_ADJUSTMENT_REASON_MIN} characters.`;
  if (length > INVENTORY_ADJUSTMENT_REASON_MAX)
    return `Reason cannot exceed ${INVENTORY_ADJUSTMENT_REASON_MAX} characters.`;
  return "";
};

const isPastDate = (value?: string) =>
  Boolean(value) && dayjs(value).isBefore(dayjs(), "day");

export default class SwageazyInventoryAdjustmentStore {
  adjustmentAPI = new SwageazyInventoryAdjustmentAPI();
  inventoryAPI = new SwageazyInventoryAPI();
  catalogProductsAPI = new CatalogProductsAPI();
  warehouseAPI = new WarehouseAPI();

  // ---- popups ----
  isAdjustOpen = false;
  isTransferOpen = false;
  isCsvOpen = false;
  isAuditOpen = false;

  // ---- shared stock (adjust + transfer) ----
  product: AdjustmentTargetProduct | null = null;
  rows: StockAdjustmentRow[] = [];
  summaries: SkuWarehouseSummary[] = [];
  blockedByWarehouse: Record<string, number> = {};
  isStockLoading = false;
  stockError = "";
  reason = "";
  isSubmitting = false;
  formError = "";
  successMessage = "";
  rowErrors: Record<string, string> = {};

  // ---- adjust: new SKU lines ----
  newLines: NewAdjustmentSkuLine[] = [];
  productSkuOptions: FilterValue[] = [];
  isProductSkusLoading = false;
  racksByWarehouse: Record<string, FilterValue[]> = {};

  // ---- transfer ----
  transferWarehouseId = "";
  destinationProduct: FilterValue | null = null;
  destinationSearchResults: FilterValue[] = [];
  destinationSearchProducts: ProductListApi[] = [];
  destinationVariants: ProductVariantApi[] = [];
  isDestinationLoading = false;
  /** Source SKU (upper-case) → destination SKU. */
  skuMapping: Record<string, string> = {};
  transferExpiryDate = "";

  // ---- CSV ----
  csvFile: File | null = null;
  csvReason = "";
  csvRowErrors: InventoryAdjustmentRowError[] = [];
  csvError = "";
  csvResult: InventoryAdjustmentResult | null = null;
  isCsvUploading = false;

  // ---- audit logs ----
  auditTitle = "";
  auditFilters: InventoryAuditLogFilters = {
    page_no: 1,
    limit: AUDIT_PAGE_LIMIT,
  };
  auditRows: InventoryAuditLogRow[] = [];
  auditTotalCount = 0;
  auditTotalPages = 0;
  isAuditLoading = false;
  auditError = "";

  // ---- product transactions ----
  isTransactionsOpen = false;
  transactionsTitle = "";
  transactionFilters: ProductTransactionFilters | null = null;
  transactionRows: ProductTransactionRow[] = [];
  transactionTotalCount = 0;
  transactionTotalPages = 0;
  isTransactionsLoading = false;
  transactionsError = "";

  constructor() {
    makeAutoObservable(this, {
      adjustmentAPI: false,
      inventoryAPI: false,
      catalogProductsAPI: false,
      warehouseAPI: false,
    });
  }

  // =========================================================
  // Shared stock rows
  // =========================================================

  get selectedRows(): StockAdjustmentRow[] {
    return this.rows.filter((row) => row.selected);
  }

  get warehouseOptions(): FilterValue[] {
    const seen = new Map<string, string>();
    this.rows.forEach((row) => {
      if (!seen.has(row.warehouse_id)) {
        seen.set(
          row.warehouse_id,
          row.city ? `${row.warehouse_id} · ${row.city}` : row.warehouse_id,
        );
      }
    });
    return Array.from(seen.entries()).map(([id, value]) => ({ id, value }));
  }

  /** Box rows visible in the transfer popup (one warehouse at a time). */
  get transferRows(): StockAdjustmentRow[] {
    if (!this.transferWarehouseId) return [];
    return this.rows.filter(
      (row) => row.warehouse_id === this.transferWarehouseId && row.on_hand > 0,
    );
  }

  get transferSelectedSourceSkus(): string[] {
    const skus = new Set<string>();
    this.transferRows
      .filter((row) => row.selected)
      .forEach((row) => skus.add(row.sku.toUpperCase()));
    return Array.from(skus);
  }

  get destinationSkuOptions(): DestinationSkuOption[] {
    const options = this.destinationVariants
      .filter((variant) => variant.active && variant.sku)
      .map((variant) => ({
        sku: String(variant.sku).toUpperCase(),
        label: [variant.name || variant.title, variant.sku]
          .filter(Boolean)
          .join(" · "),
      }));
    if (options.length > 0) return options;

    const listed = this.destinationSearchProducts.find(
      (p) => String(p.id) === this.destinationProduct?.id,
    );
    return listed?.sku
      ? [{ sku: listed.sku.toUpperCase(), label: listed.sku }]
      : [];
  }

  private resetStockState = () => {
    this.rows = [];
    this.summaries = [];
    this.blockedByWarehouse = {};
    this.stockError = "";
    this.reason = "";
    this.formError = "";
    this.successMessage = "";
    this.rowErrors = {};
    this.isSubmitting = false;
  };

  private buildStock = (details: ProductStockDetails) => {
    const byKey = new Map<string, StockAdjustmentRow>();
    (details.variants_stock || []).forEach((variant) => {
      const sku = String(variant.sku || "").trim();
      const warehouseId = String(variant.warehouse || "").trim();
      const boxId = String(variant.box_id || "").trim();
      if (!sku || !warehouseId || !boxId) return;

      const key = rowKey(sku, warehouseId, boxId);
      const existing = byKey.get(key);
      if (existing) {
        // Same SKU in a box can come back split by expiry; the API works per box.
        existing.on_hand += toInt(variant.quantity);
        return;
      }
      byKey.set(key, {
        key,
        sku,
        city: variant.city || "",
        warehouse_id: warehouseId,
        box_id: boxId,
        rack: variant.rack || "",
        on_hand: toInt(variant.quantity),
        existing_expiry_date: variant.expiry_date,
        selected: false,
        quantity: "",
        expiry_date: undefined,
        note: "",
      });
    });

    const rows = Array.from(byKey.values()).sort(
      (a, b) =>
        a.sku.localeCompare(b.sku) ||
        a.warehouse_id.localeCompare(b.warehouse_id) ||
        a.box_id.localeCompare(b.box_id),
    );

    const blockedBySku: Record<string, number> = {};
    const blockedByWarehouse: Record<string, number> = {};
    (details.blocked_stock || []).forEach((blocked) => {
      const warehouseId = String(blocked.warehouse || "");
      const qty = toInt(blocked.quantity);
      blockedByWarehouse[warehouseId] =
        (blockedByWarehouse[warehouseId] || 0) + qty;
      if (blocked.sku) {
        const key = skuWarehouseKey(blocked.sku, warehouseId);
        blockedBySku[key] = (blockedBySku[key] || 0) + qty;
      }
    });

    const summaryMap = new Map<string, SkuWarehouseSummary>();
    rows.forEach((row) => {
      const key = skuWarehouseKey(row.sku, row.warehouse_id);
      const summary = summaryMap.get(key) || {
        key,
        sku: row.sku,
        city: row.city,
        warehouse_id: row.warehouse_id,
        on_hand: 0,
        blocked: blockedBySku[key] || 0,
        available: 0,
      };
      summary.on_hand += row.on_hand;
      summary.available = summary.on_hand - summary.blocked;
      summaryMap.set(key, summary);
    });

    this.rows = rows;
    this.summaries = Array.from(summaryMap.values());
    this.blockedByWarehouse = blockedByWarehouse;
  };

  loadStock = async () => {
    if (!this.product) return;
    this.isStockLoading = true;
    this.stockError = "";
    try {
      const details = await this.inventoryAPI.getProductStockDetails(
        this.product.id,
      );
      runInAction(() => {
        this.buildStock(details || {});
      });
    } catch (error) {
      runInAction(() => {
        this.stockError = parseInventoryAdjustmentError(
          error,
          "Failed to load stock details.",
        ).message;
      });
    } finally {
      runInAction(() => {
        this.isStockLoading = false;
      });
    }
  };

  updateRow = (key: string, patch: Partial<StockAdjustmentRow>) => {
    const row = this.rows.find((r) => r.key === key);
    if (!row) return;
    Object.assign(row, patch);
    if (this.rowErrors[key]) {
      const next = { ...this.rowErrors };
      delete next[key];
      this.rowErrors = next;
    }
    this.formError = "";
    this.successMessage = "";
  };

  setRowsSelected = (keys: string[], selected: boolean) => {
    const keySet = new Set(keys);
    this.rows.forEach((row) => {
      if (keySet.has(row.key)) row.selected = selected;
    });
    if (this.isTransferOpen && selected) this.autoMapSkus();
    this.formError = "";
    this.successMessage = "";
  };

  setReason = (value: string) => {
    this.reason = value;
    this.formError = "";
    this.successMessage = "";
  };

  private applyRowErrors = (
    rowErrors: InventoryAdjustmentRowError[],
    keysByRow: string[],
  ) => {
    const mapped: Record<string, string> = {};
    rowErrors.forEach((rowError) => {
      const key = keysByRow[rowError.row - 1];
      if (key) mapped[key] = rowError.message;
    });
    this.rowErrors = mapped;
  };

  // =========================================================
  // Adjust
  // =========================================================

  openAdjust = (product: AdjustmentTargetProduct) => {
    this.resetStockState();
    this.resetNewLines();
    this.product = product;
    this.isAdjustOpen = true;
    // SKUs with stock history are merged into the options, so load stock first.
    void this.loadStock().then(() => this.loadProductSkus());
  };

  closeAdjust = () => {
    this.isAdjustOpen = false;
    this.product = null;
    this.resetStockState();
    this.resetNewLines();
  };

  private resetNewLines = () => {
    this.newLines = [];
    this.productSkuOptions = [];
    this.isProductSkusLoading = false;
  };

  /** Active catalog SKUs of the product, plus any SKU that already has stock. */
  loadProductSkus = async () => {
    if (!this.product) return;
    this.isProductSkusLoading = true;
    try {
      const variants = await this.catalogProductsAPI.fetchVariants(
        this.product.id,
      );
      runInAction(() => {
        const options = new Map<string, FilterValue>();
        (variants || [])
          .filter((variant) => variant.active && variant.sku)
          .forEach((variant) => {
            const sku = String(variant.sku).toUpperCase();
            options.set(sku, {
              id: sku,
              value: sku,
              subtitle: variant.name || variant.title || undefined,
            });
          });
        this.rows.forEach((row) => {
          const sku = row.sku.toUpperCase();
          if (!options.has(sku)) options.set(sku, { id: sku, value: sku });
        });
        this.productSkuOptions = Array.from(options.values());
      });
    } catch (error) {
      runInAction(() => {
        this.formError = parseInventoryAdjustmentError(
          error,
          "Failed to load product SKUs.",
        ).message;
      });
    } finally {
      runInAction(() => {
        this.isProductSkusLoading = false;
      });
    }
  };

  loadRacks = async (warehouseId: string) => {
    if (!warehouseId || this.racksByWarehouse[warehouseId]) return;
    try {
      const racks = await this.warehouseAPI.searchWarehouseRack(
        warehouseId,
        "",
      );
      runInAction(() => {
        this.racksByWarehouse = {
          ...this.racksByWarehouse,
          [warehouseId]: (Array.isArray(racks) ? racks : []).map(
            (rack: { id: string }) => ({
              id: String(rack.id),
              value: String(rack.id),
            }),
          ),
        };
      });
    } catch (error) {
      runInAction(() => {
        this.formError = parseInventoryAdjustmentError(
          error,
          "Failed to load racks.",
        ).message;
      });
    }
  };

  addNewLine = () => {
    const id = `${Date.now()}-${this.newLines.length}`;
    this.newLines.push({
      id,
      sku: "",
      warehouse_id: "",
      rack: "",
      box_id: "",
      quantity: "",
      expiry_date: undefined,
      note: "",
      isGeneratingBox: false,
    });
    this.formError = "";
    this.successMessage = "";
  };

  removeNewLine = (id: string) => {
    this.newLines = this.newLines.filter((line) => line.id !== id);
    this.clearRowError(`new:${id}`);
  };

  updateNewLine = (id: string, patch: Partial<NewAdjustmentSkuLine>) => {
    const line = this.newLines.find((l) => l.id === id);
    if (!line) return;
    if (
      patch.warehouse_id !== undefined &&
      patch.warehouse_id !== line.warehouse_id
    ) {
      // Rack and box belong to the warehouse.
      line.rack = "";
      line.box_id = "";
      void this.loadRacks(patch.warehouse_id);
    }
    Object.assign(line, patch);
    this.clearRowError(`new:${id}`);
    this.formError = "";
    this.successMessage = "";
  };

  /** Creates an active box on the chosen rack, same as Inward. */
  generateBoxForLine = async (id: string) => {
    const line = this.newLines.find((l) => l.id === id);
    if (!line) return;
    if (!line.warehouse_id || !line.rack) {
      this.rowErrors = {
        ...this.rowErrors,
        [`new:${id}`]: "Select a warehouse and rack before generating a box.",
      };
      return;
    }
    line.isGeneratingBox = true;
    try {
      const response = await this.inventoryAPI.generateBoxId(
        line.rack,
        line.warehouse_id,
      );
      const boxId = response?.result?.id ? String(response.result.id) : "";
      runInAction(() => {
        if (boxId) {
          line.box_id = boxId;
          this.clearRowError(`new:${id}`);
        } else {
          this.rowErrors = {
            ...this.rowErrors,
            [`new:${id}`]: "Failed to generate box ID. Please try again.",
          };
        }
      });
    } catch (error) {
      runInAction(() => {
        this.rowErrors = {
          ...this.rowErrors,
          [`new:${id}`]: parseInventoryAdjustmentError(
            error,
            "Failed to generate box ID.",
          ).message,
        };
      });
    } finally {
      runInAction(() => {
        line.isGeneratingBox = false;
      });
    }
  };

  private clearRowError = (key: string) => {
    if (!this.rowErrors[key]) return;
    const next = { ...this.rowErrors };
    delete next[key];
    this.rowErrors = next;
  };

  get adjustLineCount(): number {
    return this.selectedRows.length + this.newLines.length;
  }

  /** Row-error keys in the same order as the lines sent to the API. */
  private get adjustLineKeys(): string[] {
    return [
      ...this.selectedRows.map((row) => row.key),
      ...this.newLines.map((line) => `new:${line.id}`),
    ];
  }

  /** Returns API lines, or null after writing inline errors. */
  private buildAdjustmentLines = ():
    InventoryAdjustmentLineRequest[] | null => {
    if (!this.product) return null;
    const errors: Record<string, string> = {};
    const selected = this.selectedRows;
    const newLines = this.newLines;

    if (selected.length === 0 && newLines.length === 0) {
      this.formError =
        "Select at least one row or add a SKU, and enter a quantity.";
      return null;
    }
    if (this.adjustLineCount > INVENTORY_ADJUSTMENT_MAX_LINES) {
      this.formError = `You can adjust at most ${INVENTORY_ADJUSTMENT_MAX_LINES} lines at once.`;
      return null;
    }

    const netByWarehouse: Record<string, number> = {};
    selected.forEach((row) => {
      const qty = parseSignedInt(row.quantity);
      if (qty === null || qty === 0) {
        errors[row.key] = "Enter a non-zero whole number (e.g. 5 or -3).";
        return;
      }
      if (qty < 0 && Math.abs(qty) > row.on_hand) {
        errors[row.key] =
          `Cannot remove ${Math.abs(qty)}. Available in box: ${row.on_hand}.`;
        return;
      }
      if (!INVENTORY_BOX_ID_PATTERN.test(row.box_id)) {
        errors[row.key] = `Box '${row.box_id}' is not a valid box ID.`;
        return;
      }
      if (qty > 0 && isPastDate(row.expiry_date)) {
        errors[row.key] = "Expiry date cannot be in the past.";
        return;
      }
      if (row.note.trim().length > INVENTORY_ADJUSTMENT_NOTE_MAX) {
        errors[row.key] =
          `Note cannot exceed ${INVENTORY_ADJUSTMENT_NOTE_MAX} characters.`;
        return;
      }
      netByWarehouse[row.warehouse_id] =
        (netByWarehouse[row.warehouse_id] || 0) + qty;
    });

    // Removals must not cut into stock already blocked for PRs.
    Object.entries(netByWarehouse).forEach(([warehouseId, net]) => {
      if (net >= 0) return;
      const onHand = this.rows
        .filter((row) => row.warehouse_id === warehouseId)
        .reduce((sum, row) => sum + row.on_hand, 0);
      const blocked = this.blockedByWarehouse[warehouseId] || 0;
      if (onHand + net < blocked) {
        selected
          .filter(
            (row) =>
              row.warehouse_id === warehouseId &&
              (parseSignedInt(row.quantity) ?? 0) < 0 &&
              !errors[row.key],
          )
          .forEach((row) => {
            errors[row.key] =
              `Removal would cut into blocked stock in ${warehouseId} (on hand ${onHand}, blocked ${blocked}). Release the block first.`;
          });
      }
    });

    // New SKU lines only add stock, into an existing or generated box.
    const usedKeys = new Set(selected.map((row) => row.key));
    newLines.forEach((line) => {
      const key = `new:${line.id}`;
      const qty = parseSignedInt(line.quantity);
      if (!line.sku) {
        errors[key] = "Select a SKU.";
      } else if (!line.warehouse_id) {
        errors[key] = "Select a warehouse.";
      } else if (!INVENTORY_BOX_ID_PATTERN.test(line.box_id.trim())) {
        errors[key] = "Enter a box ID like B00001, or generate one.";
      } else if (qty === null || qty <= 0) {
        errors[key] = "Enter a positive whole number.";
      } else if (isPastDate(line.expiry_date)) {
        errors[key] = "Expiry date cannot be in the past.";
      } else if (line.note.trim().length > INVENTORY_ADJUSTMENT_NOTE_MAX) {
        errors[key] =
          `Note cannot exceed ${INVENTORY_ADJUSTMENT_NOTE_MAX} characters.`;
      } else {
        const lineKey = rowKey(line.sku, line.warehouse_id, line.box_id.trim());
        if (this.rows.some((row) => row.key === lineKey)) {
          errors[key] =
            `${line.sku} is already in ${line.box_id.toUpperCase()}. Adjust that row above instead.`;
        } else if (usedKeys.has(lineKey)) {
          errors[key] = "This SKU and box is already added.";
        }
        usedKeys.add(lineKey);
      }
    });

    const reasonError = validateReason(this.reason);
    this.rowErrors = errors;
    if (Object.keys(errors).length > 0) {
      this.formError = "Fix the highlighted rows before saving.";
      return null;
    }
    if (reasonError) {
      this.formError = reasonError;
      return null;
    }

    const productId = this.product.id;
    const existingLines = selected.map((row) => {
      const qty = parseSignedInt(row.quantity) as number;
      return {
        product_id: productId,
        sku: row.sku,
        warehouse_id: row.warehouse_id,
        box_id: row.box_id.toUpperCase(),
        quantity: qty,
        ...(qty > 0 && row.expiry_date ? { expiry_date: row.expiry_date } : {}),
        ...(row.note.trim() ? { note: row.note.trim() } : {}),
      };
    });
    const addedLines = newLines.map((line) => ({
      product_id: productId,
      sku: line.sku,
      warehouse_id: line.warehouse_id,
      box_id: line.box_id.trim().toUpperCase(),
      quantity: parseSignedInt(line.quantity) as number,
      ...(line.expiry_date ? { expiry_date: line.expiry_date } : {}),
      ...(line.note.trim() ? { note: line.note.trim() } : {}),
    }));
    return [...existingLines, ...addedLines];
  };

  submitAdjustment = async (): Promise<InventoryAdjustmentResult | null> => {
    if (this.isSubmitting) return null;
    this.formError = "";
    this.successMessage = "";
    const lines = this.buildAdjustmentLines();
    if (!lines) return null;

    const keysByRow = this.adjustLineKeys;
    this.isSubmitting = true;
    try {
      const result = await this.adjustmentAPI.createAdjustment({
        reason: this.reason.trim(),
        lines,
      });
      runInAction(() => {
        this.successMessage = `Adjustment #${result?.adjustment_id ?? "—"} saved: ${result?.line_count ?? lines.length} line(s), +${result?.total_added ?? 0} added, −${result?.total_removed ?? 0} removed.`;
        this.reason = "";
        this.newLines = [];
      });
      await this.loadStock();
      return result;
    } catch (error) {
      const parsed = parseInventoryAdjustmentError(
        error,
        "Failed to save adjustment.",
      );
      runInAction(() => {
        this.formError = parsed.message;
        this.applyRowErrors(parsed.rowErrors, keysByRow);
      });
      return null;
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  };

  // =========================================================
  // SKU transfer
  // =========================================================

  openTransfer = (product: AdjustmentTargetProduct) => {
    this.resetStockState();
    this.resetTransferState();
    this.product = product;
    this.isTransferOpen = true;
    void this.loadStock().then(() => {
      runInAction(() => {
        const options = this.warehouseOptions;
        if (options.length === 1) this.transferWarehouseId = options[0].id;
      });
    });
  };

  closeTransfer = () => {
    this.isTransferOpen = false;
    this.product = null;
    this.resetStockState();
    this.resetTransferState();
  };

  private resetTransferState = () => {
    this.transferWarehouseId = "";
    this.destinationProduct = null;
    this.destinationSearchResults = [];
    this.destinationSearchProducts = [];
    this.destinationVariants = [];
    this.isDestinationLoading = false;
    this.skuMapping = {};
    this.transferExpiryDate = "";
  };

  setTransferWarehouse = (warehouseId: string) => {
    this.rows.forEach((row) => {
      row.selected = false;
      row.quantity = "";
    });
    this.transferWarehouseId = warehouseId;
    this.rowErrors = {};
    this.formError = "";
    this.successMessage = "";
  };

  setTransferExpiryDate = (value: string) => {
    this.transferExpiryDate = value;
    this.formError = "";
  };

  searchDestinationProducts = async (searchText?: string) => {
    const trimmed = (searchText || "").trim();
    if (!trimmed) {
      this.destinationSearchResults = [];
      return;
    }
    try {
      const numericId = /^\d+$/.test(trimmed) ? Number(trimmed) : null;
      const listParams = {
        page_no: 1,
        limit: 20,
        sort_by: "id" as const,
        sort_type: SortType.DESC,
      };
      const [searchResponse, idResponse] = await Promise.all([
        this.catalogProductsAPI.listProducts({
          ...listParams,
          search: trimmed,
        }),
        numericId
          ? this.catalogProductsAPI.listProducts({
              ...listParams,
              limit: 1,
              id: numericId,
            })
          : Promise.resolve({ list: [] as ProductListApi[] }),
      ]);

      const byId = new Map<number, ProductListApi>();
      [...(idResponse?.list ?? []), ...(searchResponse?.list ?? [])].forEach(
        (product) => {
          if (product.id) byId.set(product.id, product);
        },
      );
      const products = Array.from(byId.values());
      runInAction(() => {
        this.destinationSearchProducts = products;
        this.destinationSearchResults = products.map((product) => ({
          id: String(product.id),
          value: `#${product.id} · ${product.name}`,
          subtitle: product.sku || undefined,
        }));
      });
    } catch (error) {
      runInAction(() => {
        this.destinationSearchResults = [];
        this.formError = parseInventoryAdjustmentError(
          error,
          "Failed to search products.",
        ).message;
      });
    }
  };

  selectDestinationProduct = async (option: FilterValue | null) => {
    this.destinationProduct = option;
    this.destinationVariants = [];
    this.skuMapping = {};
    this.formError = "";
    this.successMessage = "";
    if (!option) return;

    this.isDestinationLoading = true;
    try {
      const variants = await this.catalogProductsAPI.fetchVariants(
        Number(option.id),
      );
      runInAction(() => {
        this.destinationVariants = variants || [];
        this.autoMapSkus();
      });
    } catch (error) {
      runInAction(() => {
        this.formError = parseInventoryAdjustmentError(
          error,
          "Failed to load destination SKUs.",
        ).message;
      });
    } finally {
      runInAction(() => {
        this.isDestinationLoading = false;
      });
    }
  };

  /** Pre-fill the mapping when the destination has a matching or single SKU. */
  autoMapSkus = () => {
    const options = this.destinationSkuOptions;
    if (options.length === 0) return;
    const isSameProduct =
      Number(this.destinationProduct?.id) === this.product?.id;
    const next = { ...this.skuMapping };
    this.transferSelectedSourceSkus.forEach((sourceSku) => {
      if (next[sourceSku]) return;
      const match = options.find((option) => option.sku === sourceSku);
      if (match && !isSameProduct) next[sourceSku] = match.sku;
      else if (options.length === 1 && options[0].sku !== sourceSku)
        next[sourceSku] = options[0].sku;
    });
    this.skuMapping = next;
  };

  setSkuMapping = (sourceSku: string, destinationSku: string) => {
    this.skuMapping = {
      ...this.skuMapping,
      [sourceSku.toUpperCase()]: destinationSku,
    };
    this.formError = "";
    this.successMessage = "";
  };

  private buildTransferRequests = ():
    { request: SkuTransferRequest; keys: string[] }[] | null => {
    if (!this.product) return null;
    if (!this.transferWarehouseId) {
      this.formError = "Select the warehouse to transfer stock in.";
      return null;
    }
    const selected = this.transferRows.filter((row) => row.selected);
    if (selected.length === 0) {
      this.formError = "Select at least one box and enter a transfer quantity.";
      return null;
    }
    if (!this.destinationProduct) {
      this.formError = "Select the destination product.";
      return null;
    }

    const errors: Record<string, string> = {};
    selected.forEach((row) => {
      const qty = parseSignedInt(row.quantity);
      if (qty === null || qty <= 0) {
        errors[row.key] = "Enter a positive whole number.";
      } else if (qty > row.on_hand) {
        errors[row.key] =
          `Cannot transfer ${qty}. Available in box: ${row.on_hand}.`;
      }
    });
    this.rowErrors = errors;
    if (Object.keys(errors).length > 0) {
      this.formError = "Fix the highlighted rows before transferring.";
      return null;
    }

    const destinationId = Number(this.destinationProduct.id);
    const groups = new Map<string, StockAdjustmentRow[]>();
    selected.forEach((row) => {
      const sku = row.sku.toUpperCase();
      groups.set(sku, [...(groups.get(sku) || []), row]);
    });

    for (const [sourceSku, groupRows] of Array.from(groups.entries())) {
      const destinationSku = this.skuMapping[sourceSku];
      if (!destinationSku) {
        this.formError = `Map source SKU '${sourceSku}' to a destination SKU.`;
        return null;
      }
      if (destinationId === this.product.id && destinationSku === sourceSku) {
        this.formError = `SKU '${sourceSku}' cannot be transferred to itself.`;
        return null;
      }
      if (groupRows.length > SKU_TRANSFER_MAX_BOXES) {
        this.formError = `SKU '${sourceSku}' has more than ${SKU_TRANSFER_MAX_BOXES} boxes selected.`;
        return null;
      }
    }
    if (isPastDate(this.transferExpiryDate)) {
      this.formError = "Expiry date cannot be in the past.";
      return null;
    }
    const reasonError = validateReason(this.reason);
    if (reasonError) {
      this.formError = reasonError;
      return null;
    }

    const productId = this.product.id;
    return Array.from(groups.entries()).map(([sourceSku, groupRows]) => ({
      keys: groupRows.map((row) => row.key),
      request: {
        warehouse_id: this.transferWarehouseId,
        from: { product_id: productId, sku: sourceSku },
        to: { product_id: destinationId, sku: this.skuMapping[sourceSku] },
        boxes: groupRows.map((row) => ({
          box_id: row.box_id.toUpperCase(),
          quantity: Number(row.quantity),
        })),
        ...(this.transferExpiryDate
          ? { expiry_date: this.transferExpiryDate }
          : {}),
        reason: this.reason.trim(),
      },
    }));
  };

  /**
   * One API call per source SKU (the endpoint takes a single from/to pair).
   * Each call is atomic; if one fails, the earlier ones are already saved.
   */
  submitTransfer = async (): Promise<boolean> => {
    if (this.isSubmitting) return false;
    this.formError = "";
    this.successMessage = "";
    const requests = this.buildTransferRequests();
    if (!requests) return false;

    this.isSubmitting = true;
    let saved = 0;
    let movedUnits = 0;
    try {
      for (const { request, keys } of requests) {
        try {
          const result = await this.adjustmentAPI.createSkuTransfer(request);
          saved += 1;
          movedUnits += request.boxes.reduce(
            (sum, box) => sum + box.quantity,
            0,
          );
          runInAction(() => {
            this.setRowsSelected(keys, false);
            this.successMessage = `Transfer #${result?.adjustment_id ?? "—"} saved.`;
          });
        } catch (error) {
          const parsed = parseInventoryAdjustmentError(
            error,
            "Failed to transfer stock.",
          );
          const savedBeforeFailure = saved;
          runInAction(() => {
            this.applyRowErrors(parsed.rowErrors, keys);
            this.successMessage = "";
            this.formError =
              savedBeforeFailure > 0
                ? `${savedBeforeFailure} of ${requests.length} SKU transfer(s) saved. '${request.from.sku}' failed: ${parsed.message}`
                : parsed.message;
          });
          return false;
        }
      }
      runInAction(() => {
        this.successMessage = `${saved} SKU transfer(s) saved, ${movedUnits} unit(s) moved to #${this.destinationProduct?.id}.`;
        this.reason = "";
      });
      return true;
    } finally {
      if (saved > 0) await this.loadStock();
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  };

  // =========================================================
  // CSV import
  // =========================================================

  openCsv = () => {
    this.csvFile = null;
    this.csvReason = "";
    this.csvRowErrors = [];
    this.csvError = "";
    this.csvResult = null;
    this.isCsvUploading = false;
    this.isCsvOpen = true;
  };

  closeCsv = () => {
    this.isCsvOpen = false;
    this.csvFile = null;
    this.csvRowErrors = [];
    this.csvError = "";
    this.csvResult = null;
  };

  setCsvFile = (file: File | null) => {
    this.csvError = "";
    this.csvRowErrors = [];
    this.csvResult = null;
    if (file && !file.name.toLowerCase().endsWith(".csv")) {
      this.csvFile = null;
      this.csvError = "Please upload a .csv file.";
      return;
    }
    if (file && file.size > 1024 * 1024) {
      this.csvFile = null;
      this.csvError = "File is larger than 1 MB.";
      return;
    }
    this.csvFile = file;
  };

  setCsvReason = (value: string) => {
    this.csvReason = value;
    this.csvError = "";
  };

  uploadCsv = async (): Promise<InventoryAdjustmentResult | null> => {
    if (this.isCsvUploading) return null;
    this.csvError = "";
    this.csvRowErrors = [];
    this.csvResult = null;
    if (!this.csvFile) {
      this.csvError = "Choose a CSV file to upload.";
      return null;
    }
    const reasonError = validateReason(this.csvReason);
    if (reasonError) {
      this.csvError = reasonError;
      return null;
    }

    this.isCsvUploading = true;
    try {
      const result = await this.adjustmentAPI.importAdjustmentCsv(
        this.csvFile,
        this.csvReason.trim(),
      );
      runInAction(() => {
        this.csvResult = result;
        this.csvFile = null;
        this.csvReason = "";
      });
      return result;
    } catch (error) {
      const parsed = parseInventoryAdjustmentError(
        error,
        "Failed to import adjustment CSV.",
      );
      runInAction(() => {
        this.csvError = parsed.message;
        this.csvRowErrors = parsed.rowErrors;
      });
      return null;
    } finally {
      runInAction(() => {
        this.isCsvUploading = false;
      });
    }
  };

  // =========================================================
  // Audit logs
  // =========================================================

  openAudit = (product: AdjustmentTargetProduct) => {
    this.auditTitle = product.name;
    this.auditFilters = {
      product_id: product.id,
      page_no: 1,
      limit: AUDIT_PAGE_LIMIT,
    };
    this.auditRows = [];
    this.auditTotalCount = 0;
    this.auditTotalPages = 0;
    this.auditError = "";
    this.isAuditOpen = true;
    void this.loadAuditLogs();
  };

  closeAudit = () => {
    this.isAuditOpen = false;
    this.auditRows = [];
    this.auditError = "";
  };

  setAuditFilters = (patch: Partial<InventoryAuditLogFilters>) => {
    this.auditFilters = {
      ...this.auditFilters,
      ...patch,
      page_no: patch.page_no ?? 1,
    };
    void this.loadAuditLogs();
  };

  loadAuditLogs = async () => {
    this.isAuditLoading = true;
    this.auditError = "";
    try {
      const response = await this.adjustmentAPI.getAuditLogs(this.auditFilters);
      runInAction(() => {
        this.auditRows = response?.list || [];
        this.auditTotalCount = response?.total_count || 0;
        this.auditTotalPages = response?.total_pages || 0;
      });
    } catch (error) {
      runInAction(() => {
        this.auditRows = [];
        this.auditError = parseInventoryAdjustmentError(
          error,
          "Failed to load audit logs.",
        ).message;
      });
    } finally {
      runInAction(() => {
        this.isAuditLoading = false;
      });
    }
  };

  // =========================================================
  // Product transactions (stock ledger)
  // =========================================================

  openTransactions = (product: AdjustmentTargetProduct) => {
    this.transactionsTitle = product.name;
    this.transactionFilters = { product_id: product.id, page_no: 1 };
    this.transactionRows = [];
    this.transactionTotalCount = 0;
    this.transactionTotalPages = 0;
    this.transactionsError = "";
    this.isTransactionsOpen = true;
    void this.loadTransactions();
  };

  closeTransactions = () => {
    this.isTransactionsOpen = false;
    this.transactionRows = [];
    this.transactionsError = "";
  };

  setTransactionFilters = (
    patch: Partial<Omit<ProductTransactionFilters, "product_id">>,
  ) => {
    if (!this.transactionFilters) return;
    this.transactionFilters = {
      ...this.transactionFilters,
      ...patch,
      page_no: patch.page_no ?? 1,
    };
    void this.loadTransactions();
  };

  loadTransactions = async () => {
    const filters = this.transactionFilters;
    if (!filters) return;
    this.isTransactionsLoading = true;
    this.transactionsError = "";
    try {
      const response = await this.adjustmentAPI.getProductTransactions(filters);
      runInAction(() => {
        this.transactionRows = response?.list || [];
        this.transactionTotalCount = response?.total_count || 0;
        this.transactionTotalPages = response?.total_pages || 0;
      });
    } catch (error) {
      runInAction(() => {
        this.transactionRows = [];
        this.transactionsError = parseInventoryAdjustmentError(
          error,
          "Failed to load transactions.",
        ).message;
      });
    } finally {
      runInAction(() => {
        this.isTransactionsLoading = false;
      });
    }
  };
}
